// After a NEW account is created, the signup response carries a thin user. The onboarding fields
// (onboardingActive, signupSource, signupContext, onboarding) only come from GET /auth/me, so
// AuthContext re-fetches it once and swaps the user in, letting the welcome card and the second
// ask render in the same session without a reload. Pure: no React, the fetch and the token check
// are injected so the race handling is unit-tested.
import type { User } from "../integrations/mongodb/types";
import type { CurrentUserResult } from "../integrations/mongodb/clientAuthService";

export type AuthMethod = "email" | "google" | "phone" | "facebook";

// Email signUp always creates an account. Google and phone say so via isNewUser. Facebook is false
// here on purpose: its fb_new redirect path already hydrates from /auth/me in initializeAuth.
export const isNewAccountAuth = ({ method, isNewUser }: { method: AuthMethod; isNewUser?: boolean }): boolean => {
  if (method === "email") return true;
  if (method === "google" || method === "phone") return isNewUser === true;
  return false;
};

// JSON dates arrive as strings. Spread, never pick: every onboarding field must survive.
export const normalizeApiUser = (apiUser: User): User => ({
  ...apiUser,
  createdAt: new Date(apiUser.createdAt),
  updatedAt: new Date(apiUser.updatedAt),
});

// Returns the fresh /auth/me user, the fallback when /auth/me fails (sign-in must never break),
// or null when the session changed or was signed out while the request was pending, in which case
// the caller must apply nothing. isCurrentToken runs AFTER fetchMe settles, never before.
export const resolvePostSignupUser = async ({
  token,
  fallbackUser,
  fetchMe,
  isCurrentToken,
}: {
  token: string;
  fallbackUser: User;
  fetchMe: (token: string) => Promise<CurrentUserResult>;
  isCurrentToken: (token: string) => boolean;
}): Promise<User | null> => {
  let result: CurrentUserResult | undefined;
  try {
    result = await fetchMe(token);
  } catch {
    result = undefined;
  }
  if (!isCurrentToken(token)) return null;
  if (result?.status === "authenticated") return normalizeApiUser(result.user);
  return fallbackUser;
};
